import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';

import { documentsEndpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { getDebouncedInvalidator } from '@/hooks/utils/debounceQueryInvalidation';

/**
 * Interface for useDocumentContent hook parameters
 */
interface UseDocumentContentParams {
  collectionId?: string;
  datasourceId?: string;
  documentId?: string;
  enabled?: boolean;
}

/**
 * Interface for useDocumentContent hook return value
 */
interface UseDocumentContentReturn {
  content: string | undefined;
  isLoading: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
  invalidateCache: () => void;
}

export const onDocumentContentUpdated = (
  queryClient: QueryClient,
  collectionId: string,
  datasourceId: string,
  documentId: string
) => {
  queryClient.invalidateQueries({
    queryKey: ['document-content', collectionId, datasourceId, documentId],
  });
};

export const useDocumentContent = ({
  collectionId,
  datasourceId,
  documentId,
  enabled = true,
}: UseDocumentContentParams): UseDocumentContentReturn => {
  const queryClient = useQueryClient();

  const queryKey = ['document-content', collectionId, datasourceId, documentId] as const;

  const isEnabled = enabled && !!collectionId && !!datasourceId && !!documentId;

  const {
    data: content,
    isLoading,
    error,
    isSuccess,
    refetch,
  } = useQuery<string, Error>({
    queryKey,
    queryFn: () =>
      callGet(documentsEndpoints.exportDocument(collectionId!, datasourceId!, documentId!), {
        responseType: 'text',
      }),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    retry: (failureCount, error) => {
      // Don't retry if it's a 404 (document not found)
      if (error && 'status' in error && error.status === 404) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });

  /**
   * Invalidates the cache for this specific document
   * Useful when the document has been updated and we want fresh content
   * Uses debounced invalidation to prevent rate limiting
   */
  const invalidateCache = () => {
    const invalidator = getDebouncedInvalidator(queryClient, 250);
    invalidator.invalidate([...queryKey]);
  };

  return { content, isLoading, error, isSuccess, refetch, invalidateCache };
};
